import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { LockMode, Valuation } from "../api/types";
import { useNow } from "../hooks/useNow";
import { formatAge } from "../lib/format";
import { formatIndex, indexAge, metricLabel } from "../lib/valuation";
import { Ahead, Field, FieldRow, Mark, Qual, Val, W } from "./Field";
import { LockGlyph } from "./LockGlyph";
import {
  PREOPEN_PROB_NOTE,
  preopenAheadCushion,
  preopenAheadProbLabel,
  preopenAheadProjected,
  preopenCushion,
  preopenCushionTitle,
  preopenLostNote,
  preopenPhase,
  preopenProbLabel,
  preopenProjected,
  preopenSide,
} from "../lib/preopen";
import {
  accuracyLabel,
  aheadCushionAmount,
  aheadLabel,
  aheadProbFlipLabel,
  aheadProjectedValue,
  aheadRequiredLabel,
  attainableLabel,
  attainableTitle,
  lateOnVenueNote,
  PROB_UNVERIFIED_NOTE,
  probFlipLabel,
  venueTargetLabel,
  checkSummary,
  coverageLabel,
  coverageNote,
  cushionAmount,
  cushionArrow,
  cushionTitle,
  cushionTone,
  diffLabel,
  diffTone,
  emptyNote,
  finalAgrees,
  finalDriftLabel,
  finalDriftTone,
  finalOutcomeLabel,
  finalSideLabel,
  finalTitle,
  finalWinnerTone,
  flipTitle,
  flipTone,
  forecastErrNote,
  forecastLeverNote,
  forecastErrValue,
  forecastGainLabel,
  forecastLagLabel,
  forecastLive,
  forecastNote,
  forecastSpotNote,
  forecastTone,
  forecastValue,
  frozenLimit,
  frozenLimitNote,
  frozenOutcome,
  frozenPath,
  lockCounting,
  deadPrefixSeconds,
  lockView,
  projectedValue,
  rawAge,
  realDataLagLabel,
  requiredEqualsCushion,
  requiredLabel,
  vsCurrentLabel,
  vsCurrentTone,
  SOURCE_NOTE,
  strikeCheckNote,
  strikeCheckTone,
  unknownSeconds,
} from "../lib/twaplock";
import { SubsystemAccountSelect } from "./SubsystemAccountSelect";

/**
 * Сверка индекса и замок исхода окна — НИЖНИЙ ярус карточки рынка
 * (components/MarketOverview.tsx). Часть ИНФОРМАЦИОННАЯ: ничего не делает.
 *
 * # Зачем она
 *
 * Отчёт TWAP опаздывает на три секунды: значение с меткой t — это среднее сырого
 * ряда по `[t−W−2, t−3]`. Отсюда два следствия, которых иначе на экране не
 * видно. Первое: то же число собирается НАМИ на три секунды раньше, чем его
 * опубликует источник, и сверка отвечает, вправе ли мы им пользоваться. Второе:
 * ближе к концу окна итог перестаёт зависеть от рынка вовсе — данных, влияющих
 * на него, больше не поступит.
 *
 * Запертость наступает за ДВЕ секунды до конца, а не за три, и число «неизвестно»
 * считается по ней. Задержка публикации не постоянна: у 16 % отчётов окно
 * кончается на `t−2`, а не на `t−3` (замер 2026-08-14), и объявить исход
 * определённым по тройке значило бы предложить ставку на 0.99 по ещё не
 * решённому исходу в каждом шестом окне. Значение при этом по-прежнему считается
 * по тройке — она верна для большинства отчётов. Разбор — twap.SettleDelay.
 *
 * # Почему одна строка, а не две колонки
 *
 * Ярус был двухколоночным и стоил 160 пикселей — четверть ЛИПКОЙ полосы, то есть
 * четверть экрана, отнятую у накопления участников и ленты навсегда, а не на
 * время прокрутки. Столько стоят два разных ответа, и цену им назначили разную:
 *
 *   замок («можно ли ещё перевернуть исход») — торговый сигнал, и он остаётся
 *   на экране целиком, строкой;
 *
 *   сверка («сходимся ли мы с биржей») — диагностика доверия к числу, читаемая
 *   раз в сеанс или при подозрении. Разбор открывается кнопкой, а на экране от
 *   яруса остаётся НАШ ОПЕРЕЖАЮЩИЙ ОТЧЁТ — то самое число, ради которого сверку
 *   и заводили: где метрика окажется через три секунды, когда источник её
 *   опубликует. Тон ему задаёт сверка (lib/twaplock.ts, checkSummary): не
 *   сошлась — верить числу нельзя, и это видно по цвету.
 *
 * Предикшен стоит СТОЛБИКОМ под страйком и индексом: три момента одной
 * величины, которые сравнивают глазом по вертикали.
 *
 * # Достройка: ВТОРОЕ число рядом, а не вместо
 *
 * Спот биржи едет по тому же проводу раньше сырого индекса, поэтому хвост
 * достраивается оценкой — не прогнозом цены BTC, а восстановлением уже
 * случившегося, но ещё не доехавшего. Толк от этого главным образом не в
 * секундах: почти всё окно требуемый скачок равен `|индекс − страйк|`, то есть
 * его точность равна точности знания уровня, а её достройка режет вдвое
 * ($4.30 → $2.15 RMSE).
 *
 * Поэтому достроенные числа стоят ЧЕРЕЗ СТРЕЛКУ от сегодняшних — и у предикшена,
 * и у запаса, и у требуемого скачка. Замени они первое число собой, и оператор
 * перестал бы видеть, насколько отстал провод; а показывать поправку, когда она
 * совпала до цента, значит рисовать две одинаковые цифры (lib/twaplock.ts).
 *
 * Замок при этом остаётся ЧУЖИМ для достройки: `settled`, состояние и плашка
 * тревоги считаются только по настоящему ряду. Один неверный аларм на цене 0.99
 * съедает девяносто девять верных, и права двигать его оценка не получает.
 *
 * Разбор — в окне поверх экрана, а не раскрытием на месте: раскрытие двигало бы
 * липкую полосу под курсором, целящимся в кнопку ставки. В окне при этом стоит
 * ТА ЖЕ колоночная сетка — три числа одной величины сравнивают глазом по
 * вертикали, и превратить их в бегущую строку значит потерять смысл сверки.
 *
 * # Чего она не утверждает
 *
 * Всё это — про число, которое раздаёт Polymarket. Что оно совпадает с
 * подписанным отчётом Chainlink, НЕ ПРОВЕРЕНО и изнутри провода не проверяется.
 * Без оговорки аларм читается как гарантия исхода, а он утверждение о сверяемом
 * числе, — поэтому она присутствует всегда, но значком «ⓘ» (см. SourceHint), а
 * не строкой в семьдесят знаков: в липкой полосе такая строка оплачивается всей
 * высотой экрана. Целиком оговорка стоит в окне разбора.
 *
 * # Предел свежести
 *
 * Кадр сырого значения едет 1.6 с (p90 2.1 с), опрос идёт раз в секунду. Значит
 * аларм появляется примерно через 2.6 с после того, как решающие данные
 * возникли, — около `конец окна − 0.4 с`. Для чтения глазами это годится, для
 * действия нет: заявка к этому моменту должна была бы уже стоять в стакане.
 *
 * Логика вся в lib/twaplock.ts: компонентных тестов в проекте нет, поэтому в
 * .tsx остаётся разметка. Расстояния и слоты — в components/Field.tsx: числа
 * этих двух ярусов мигают посекундно, и держать их на месте приходится общей
 * сеткой, а не глазомером по месту.
 */
export function IndexCheck({ v }: { v: Valuation | undefined }) {
  const [details, setDetails] = useState(false);

  // Оценки нет вовсе — молчим: строка индекса над нами уже назвала причину, а
  // повторять её вторым сообщением значит удваивать шум на пустом месте.
  if (!v) return null;

  const lock = v.lock;
  const view = lockView(v);
  const summary = checkSummary(v);

  const unknown = unknownSeconds(lock);
  const counting = lockCounting(v, lock);
  const deadPrefix = deadPrefixSeconds(lock);
  const sameAsCushion = requiredEqualsCushion(lock);
  const lost = coverageNote(lock);

  // Достройка: второе число рядом с первым, а причина его отсутствия — словами.
  const built = forecastValue(v.check);
  const gain = forecastGainLabel(v.check);
  const fwdNote = forecastNote(v.check);
  const fwdRequired = aheadRequiredLabel(lock);
  // Достижимое среднее — второе число строки «нужен скачок». Первое место, где
  // видно СООТНОШЕНИЕ, а не два несравнимых числа.
  const attainable = attainableLabel(lock);
  // Вероятность переворота и биржевая ось. Вероятность помечена «не сверено»
  // значком: до первого сошедшегося отчёта это модельное число.
  const prob = probFlipLabel(lock);
  const fwdProb = aheadProbFlipLabel(lock);
  const venueTarget = venueTargetLabel(lock);
  const lateNote = lateOnVenueNote(lock);

  return (
    <>
      {/* Окно кончилось — на месте прогноза стоит ИТОГ.
          Заменяется ИМЕННО этот ярус, а не оба: прогноз отвечал на вопросы
          идущего окна («где метрика окажется через три секунды», «сколько у нас
          форы») и считался от текущей секунды — у закрытого окна это числа про
          другое время. Замок остаётся: он считается по хвосту САМОГО окна и
          гаснет сам, когда хвост из раздачи уехал. В первые секунды после конца
          он и есть лучший ответ об исходе — раньше записи итога (полторы
          секунды) и раньше подтверждения биржи (до двух минут). */}
      {v.final && <WindowResult v={v} />}

      {/* ЯРУС ПРОГНОЗА и ЯРУС ЗАМКА — две разные строки, а не одна с переносом.
          Раньше они делили ряд, и на узком экране замок срывался вниз сам:
          перенос ставил его то в одну строку с прогнозом, то в другую, и место
          торгового сигнала на экране гуляло. Отдельная строка с той же
          разделительной линией, что у ярусов выше, держит его на месте. */}
      {!v.final && (
      <div className="border-b border-[var(--color-border)] px-4 py-1.5 text-xs">
        <FieldRow>
          {/* Наш отчёт СТОЛБИКОМ под страйком и индексом: те же подпись слева,
              число моноширинным справа от неё и тот же формат. Все три — одна
              величина в разные моменты, и сравнивают их глазом по вертикали;
              слот подписи у всех трёх один и тот же, иначе столбика не выйдет.

              Ŝ — то же S, что в строке выше, но ОЦЕНЁННОЕ: крышка над буквой и
              означает оценку. Обозначение вместо слова по тому же счёту, что K и
              S этажом выше: «предикшен» это девять знаков ЛИПКОЙ строки, а
              прочитывают их один раз.

              Место под достройку держится, пока она ЖИВА, а не пока её видно:
              совпав с проводом до цента, второе число не рисуется — и без слота
              полстроки ездило бы туда-сюда каждую секунду. */}
          <Field
            tag
            label="Ŝ"
            title={summary.title}
            w={forecastLive(v.check) ? W.predBuilt : W.pred}
          >
            <Val
              w={W.price}
              color={summary.tone === "warn" ? "var(--color-warn)" : undefined}
            >
              {formatIndex(summary.value)}
            </Val>
            {/* Метка — ДОКУДА дотягиваются настоящие данные, а скобки —
                отклонение от опубликованного. Первое отвечает «чем это
                посчитано», второе — «на сколько метрика вот-вот сдвинется»;
                ради второго на оба числа и смотрят. Оба прижаты к своему
                числу: относятся они к нему, а не к соседнему. */}
            <Mark w={W.mark} title="докуда дотягиваются настоящие данные этого числа">
              {realDataLagLabel()}
            </Mark>
            {vsCurrentLabel(v.check?.ahead, v.index) !== "" && (
              <Qual
                w={W.delta}
                color={deltaColor(vsCurrentTone(v.check?.ahead, v.index))}
                title="на столько наш отчёт отличается от опубликованного"
              >
                ({vsCurrentLabel(v.check?.ahead, v.index)})
              </Qual>
            )}
            {/* Достройка — ВТОРЫМ числом через стрелку, а не вместо первого.
                Сегодняшнее обязано остаться на экране: иначе поправка перестанет
                читаться как поправка, и не будет видно, насколько отстал провод. */}
            {built !== undefined && (
              <>
                <Ahead
                  w={W.price}
                  title={
                    "то же число по ряду, ДОСТРОЕННОМУ спотом биржи: спот едет по тому " +
                    "же проводу раньше сырого индекса, и хвост восстанавливается оценкой. " +
                    "Это не прогноз цены — это уже случившееся, но ещё не доехавшее"
                  }
                >
                  {formatIndex(built)}
                </Ahead>
                {/* У достроенного числа свежесть ХУЖЕ, и метка это называет:
                    оно меняет свежесть последней настоящей секунды на дальность
                    метки. Без неё второе число читалось бы просто «лучше». */}
                {forecastLagLabel(v.check) !== "" && (
                  <Mark
                    w={W.mark}
                    title={
                      "докуда дотягиваются НАСТОЯЩИЕ данные у этого числа: достройка " +
                      "сдвигает метку вперёд, но последняя не выдуманная секунда от " +
                      "неё дальше — на три секунды задержки плюс число дописанных"
                    }
                  >
                    {forecastLagLabel(v.check)}
                  </Mark>
                )}
                {vsCurrentLabel(built, v.index) !== "" && (
                  <Qual
                    w={W.delta}
                    color={deltaColor(vsCurrentTone(built, v.index))}
                    title="на столько достроенный отчёт отличается от опубликованного"
                  >
                    ({vsCurrentLabel(built, v.index)})
                  </Qual>
                )}
              </>
            )}
          </Field>

          {gain !== "" && (
            <span
              className="text-[var(--color-muted)]"
              title={
                "на столько раньше провода мы знаем это число. Показано по СТРОГОМУ " +
                "краю: лаг агрегации неразличим внутри секунды, и обещать запас, " +
                "которого может не быть, нельзя"
              }
            >
              {gain}
            </span>
          )}
          {fwdNote !== "" && (
            <span
              className={
                forecastTone(v.check) === "warn" ? "text-amber-300" : "text-[var(--color-muted)]"
              }
            >
              {fwdNote}
            </span>
          )}
          {/* Сдвиг источника — рядом с числом: он и говорит, ЗАЧЕМ это число, —
              на столько метрика вот-вот уедет у самой биржи. */}
          {summary.drift !== "" && <span className="text-[var(--color-muted)]">{summary.drift}</span>}
          {/* Замечание сверки — тут же: сжав ярус до одного числа, мы обязаны
              оставить на экране то, ради чего сверку вообще читают. */}
          {summary.note !== "" && <span className="text-amber-300">{summary.note}</span>}

          {/* Кнопка — у ПРАВОГО края, а не сразу за числами: она не величина, и
              место ей там, где её не сдвинет ни появившееся замечание, ни
              пропавшая поправка. Целятся в неё курсором, а движущаяся мишень
              стоит промаха. */}
          <button
            type="button"
            onClick={() => setDetails(true)}
            className="ml-auto text-[var(--color-muted)] underline decoration-dotted hover:text-[var(--color-fg)]"
            title="сырой ряд, наш пересчёт, сверка с источником и её точность"
          >
            подробнее
          </button>
        </FieldRow>
      </div>
      )}

      {/* У ЗАКОНЧИВШЕГОСЯ окна ярус замка показывается, только пока замок ещё
          считается. Без замка он печатал бы «замок не считается: сырой ряд не
          приехал» — фразу верную для идущего окна и неверную здесь: ряд в
          порядке, просто секунды этого окна уехали из раздачи, а на вопрос
          «чем кончилось» уже ответила строка итога выше. */}
      {/* До фиксации страйка на месте замка стоит ПРЕДСТАРТ. Замок отвечает
          «можно ли ещё перевернуть исход», а до открытия перевернуть можно
          всегда — спрашивать его не о чем, и строка «замок не считается» висела
          бы все полторы минуты, пока стакан следующего окна уже торгуется. */}
      {v.preopen && <PreopenRow v={v} />}

      {!v.preopen && (!v.final || lock) && (
      <div className="px-4 py-1.5 text-xs">
        <FieldRow>
          {/* Значок вместо надписи «ЗАМОК ИСХОДА»: он тот же, что на сквозных
              карточках сигналов, и говорит состояние ФОРМОЙ ДУЖКИ — замкнута,
              когда исход определён. Двенадцать знаков заглавными освободились,
              а состояние стало видно раньше, чем читается текст. */}
          {lock && <LockGlyph state={lock.state} label={view.label} />}
          {!lock && <Caption>замок исхода</Caption>}
          {!lock && <span className="text-amber-300">{view.label}</span>}
          {lock && (
            <>
              {/* Показывается то число, которое ДВИЖЕТСЯ. Пока итоговое окно
                  впереди, «неизвестно» прибито к его ширине и стоит на месте
                  четыре с половиной минуты из пяти — замерший счётчик читается
                  как сломанный. Двигается в это время обратный отсчёт до начала
                  окна, он и идёт на экран.

                  Слот один на оба состояния: подпись меняется ЦЕЛИКОМ, с
                  «неизвестно» на циферблат, и без запаса ширины вся строка
                  прыгала бы на этой смене — ровно в ту минуту, когда на неё и
                  смотрят. */}
              {counting ? (
                <Field
                  label="неизвестно"
                  w={W.counter}
                  title={
                    "сколько секунд итогового окна ещё не наступило по МОДАЛЬНОЙ " +
                    "задержке — по ней считается требуемый скачок, и их всегда на три " +
                    "меньше, чем до конца окна. Исход при этом объявляется определённым " +
                    "на секунду позже: у каждого шестого отчёта секунда t−2 в итог входит"
                  }
                >
                  <Val>{unknown === null ? "—" : `${unknown} с`}</Val>
                </Field>
              ) : (
                /* Мёртвый префикс вместо «замок закроется через». Число то же,
                   но вопрос другой: пока итоговое окно впереди, ход, который
                   откатится раньше его начала, вносит в исход РОВНО НОЛЬ.
                   «Начнётся через 27 с» читается как ожидание, «продержаться
                   27 с» — как требование, и оператору нужно второе.

                   Значок песочных часов, а не циферблата: это НЕ обратный
                   отсчёт до события, а срок, который надо выстоять. */
                <Field
                  label="⧗"
                  w={W.counter}
                  title={
                    "сколько секунд ход обязан ПРОДЕРЖАТЬСЯ, чтобы вообще попасть в " +
                    "итог: итоговое окно ещё не началось, и всё, что откатится раньше, " +
                    "не вносит в исход ничего. Число приходит с сервера — задержка " +
                    "публикации измерена, а не назначена интерфейсом"
                  }
                >
                  <Val>{deadPrefix === null ? "—" : `${deadPrefix} с`}</Val>
                </Field>
              )}
              {/* Запас — СТРЕЛКОЙ и величиной. Направление читается раньше цифр
                  и тем же значком, что в строке индекса выше.

                  Слоты всех трёх полей яруса держатся по тому же признаку, что
                  и слот Ŝ выше, — по СОСТОЯНИЮ подсистемы, а не по наличию
                  `lock.ahead` в кадре. Признаки расходятся посекундно: сервер
                  кладёт тень замка только когда достройке досталась хоть одна
                  спот-секунда, а при форе около секунды две ленты по 1 Гц
                  расходятся фазой и то и дело не дают ни одной. Гейт по кадру
                  схлопывал бы разом три поля — треть яруса, — и строка ездила
                  бы ровно в тех секундах, ради которых слоты и заведены. */}
              <Field
                label="запас"
                w={forecastLive(v.check) ? W.cushionBuilt : W.cushion}
                title={cushionTitle(lock)}
              >
                <Val w={W.money} color={deltaColor(cushionTone(lock.cushion))}>
                  {cushionArrow(lock.cushion)} {cushionAmount(lock.cushion)}
                </Val>
                {aheadCushionAmount(lock) !== "" && (
                  <Ahead w={W.money}>{aheadCushionAmount(lock)}</Ahead>
                )}
              </Field>
              {/* Требуемый скачок — только когда он ОТЛИЧАЕТСЯ от запаса. Пока
                  неизвестно всё окно, формула W·C/u при u = W даёт ровно C, и
                  второе такое же число не сообщает ничего.

                  Цвет — по НАПРАВЛЕНИЮ хода, а не по величине: красный означает
                  «индекс должен вырасти», зелёный — «должен упасть». Привычка
                  терминала тут ровно к месту, потому что вопрос именно «куда». */}
              {!sameAsCushion && (
                <Field
                  label="нужен скачок"
                  w={forecastLive(v.check) ? W.requiredBuilt : W.required}
                  title={
                    `${flipTitle(lock.cushion)}. Требование к СРЕДНЕМУ за оставшиеся ` +
                    "секунды: ход вдвое больший, продержавшийся половину остатка, даёт " +
                    `то же${view.basis === "" ? "" : `. Порог: ${view.basis}`}`
                  }
                >
                  <Val w={W.money} color={flipColor(lock.cushion)}>
                    {requiredLabel(lock)}
                  </Val>
                  {fwdRequired !== "" && <Ahead w={W.money}>{fwdRequired}</Ahead>}
                </Field>
              )}
              {/* Достижимое среднее — рядом и той же ширины. Строка читается как
                  «нужен скачок 20 $ · достижимо 15 $», и это первое место, где
                  оператор видит соотношение, а не два несравнимых числа.

                  Именно СРЕДНЕЕ, а не пик: требование — к среднему по
                  неизвестным секундам, и сравнение с пиком вдвое завышало бы
                  шансы переворота. Пик остался в окне разбора. */}
              {!sameAsCushion && attainable !== "" && (
                <Field label="достижимо" w={W.required} title={attainableTitle(lock)}>
                  <Val w={W.money}>{attainable}</Val>
                </Field>
              )}
              {/* Целевая цена ПЛОЩАДКИ: куда должен уйти спот. Это тот вопрос,
                  который оператор задаёт глядя на биржу, а панель до сих пор
                  отвечала на другой — в долларах индекса.

                  Курс USDT/USD для этого не нужен: требуемое движение это
                  приращение, а цель считается приращением от цены той же
                  площадки, и базис в разности сокращается целиком. */}
              {!sameAsCushion && venueTarget !== "" && (
                <Field
                  label="цель биржи"
                  w={W.required}
                  title={
                    "куда должен уйти СПОТ, чтобы исход перевернулся, — в валюте самой " +
                    "площадки. Считается приращением от её же цены, поэтому курс не нужен. " +
                    "Требование там БОЛЬШЕ индексного: движение биржи попадает в индекс " +
                    "через лаг агрегации, значит на часах биржи неизвестных секунд меньше. " +
                    "Решение при этом считается по СТРОГОМУ краю и этого числа не читает"
                  }
                >
                  <Val w={W.money}>{venueTarget}</Val>
                </Field>
              )}
              {/* Вероятность ПЕРЕВОРОТА ведущей стороны — рядом с требованием и
                  ПОМЕЧЕННАЯ. Значок «ⓘ» тот же, что у оговорки об источнике, и
                  означает то же: число показывается, но за него ещё не
                  поручились.

                  Со своей поправкой достройки, как запас и итог. Без неё строка
                  противоречила себе: вероятность считается от запаса, а слева
                  стоял запас тени — другой, вплоть до знака. */}
              {prob !== "" && (
                <Field
                  label="вероятность ⓘ"
                  w={forecastLive(v.check) ? W.probBuilt : W.prob}
                  title={PROB_UNVERIFIED_NOTE}
                >
                  <Val w={W.sigma}>{prob}</Val>
                  {/* Поправка достройки — ТЕМ ЖЕ приёмом, что у запаса и итога, и
                      это не единообразие ради единообразия. Вероятность считается
                      от запаса, а запас едет тут же со своей поправкой: покажи
                      одно без другого — и два соседних числа скажут разное. */}
                  {fwdProb !== "" && <Ahead w={W.sigma}>{fwdProb}</Ahead>}
                </Field>
              )}
              {/* Третье число яруса: каким ЗНАЧЕНИЕМ кончится метрика, если
                  цена замрёт. Не «Up/Down» — исход производен от сравнения со
                  страйком, а страйк стоит строкой выше; само же число отвечает
                  на вопрос, которого сравнение не закрывает: насколько итог
                  разойдётся со страйком. Сторону при этом говорит стрелка у
                  запаса, а словами она стоит в подсказке. */}
              {projectedValue(lock) !== undefined && (
                <Field
                  label="итог"
                  w={forecastLive(v.check) ? W.outcomeBuilt : W.outcome}
                  title={
                    `${metricLabel(v)} на конец окна, если индекс останется на текущем ` +
                    "уровне. Не прогноз рынка — арифметика по уже известным секундам " +
                    "плюс замершая цена"
                  }
                >
                  <Val w={W.price}>{formatIndex(projectedValue(lock))}</Val>
                  {aheadProjectedValue(lock) !== undefined && (
                    <Ahead w={W.price}>{formatIndex(aheadProjectedValue(lock))}</Ahead>
                  )}
                  {/* Сторона СРАЗУ за числом и цветом: число отвечает «сколько»,
                      сторона — «в чью пользу», и второе читается раньше первого.
                      Цвета те же, что у стрелки запаса слева, — она про то же
                      самое направление, и разойтись им нельзя. */}
                  {frozenOutcome(lock) !== "" && (
                    <span
                      className="num font-medium"
                      style={{ color: deltaColor(cushionTone(lock.cushion)) }}
                    >
                      {frozenOutcome(lock)}
                    </span>
                  )}
                </Field>
              )}
              {lost !== "" && <span className="text-amber-300">{lost}</span>}
              {/* «На бирже уже поздно» — ПРИГЛУШЁННЫМ тоном, а не тревожным, и
                  это не косметика. Подсказка полезна, но она НЕ утверждение о
                  запертости исхода: то приглашает ставку у единицы и считается
                  по самой осторожной комбинации. Два утверждения едут рядом с
                  разными формулировками и разным тоном — иначе оператор прочтёт
                  их как одно. */}
              {lateNote !== "" && (
                <span className="text-[var(--color-muted)]">{lateNote}</span>
              )}
            </>
          )}
          {/* Переключатель порога и оговорка — у ПРАВОГО края: это не величины
              яруса, и стоять они обязаны там, где их не сдвинет ни появившийся
              требуемый скачок, ни пропавшая поправка достройки. */}
          <span className="ml-auto flex items-baseline gap-x-[var(--gap-hug)]">
            <ModePicker current={lock?.mode} />
            <SourceHint />
            <SubsystemAccountSelect subsystem="lock" />
          </span>
        </FieldRow>

        {/* Тревога — ВО ВСЮ ширину карточки: она про исход окна целиком, и на
            неё смотрят вместо чисел, а не рядом с ними. Сворачиванию не
            подлежит ни при каких условиях. */}
        {view.level !== "none" && (
          <p
            className={`mt-1 rounded px-3 py-1 ${
              view.level === "alarm"
                ? "border border-[var(--color-sell)]/40 bg-[var(--color-sell)]/10 text-[var(--color-sell)]"
                : "border border-amber-500/30 bg-amber-500/10 text-amber-300"
            }`}
          >
            {view.label}
            {view.basis !== "" && <span className="opacity-70"> — {view.basis}</span>}{" "}
            <SourceHint />
          </p>
        )}
      </div>
      )}

      {details && <CheckDetails v={v} onClose={() => setDetails(false)} />}
    </>
  );
}

/**
 * Ярус предстарта: фаза окна страйка, запас, вероятность и итог.
 *
 * Те же поля и те же слоты, что у яруса замка, — и не из любви к единообразию.
 * Ярус меняется на замок в секунду открытия окна, прямо под курсором оператора,
 * и если числа при этом переедут на другие места, первые секунды живого окна
 * уйдут на то, чтобы найти их заново. Отличие одно: вместо счётчика замка стоит
 * фаза ОКНА СТРАЙКА, потому что до открытия движется именно оно.
 *
 * Требуемого скачка, достижимого среднего и цели биржи здесь нет: все три
 * отвечают на вопрос «чем перевернуть исход», а до фиксации страйка он
 * переворачивается любым ходом цены. Переключателя порога нет по той же
 * причине — порог нужен решению замка, а решения на предстарте нет.
 *
 * Логика — в lib/preopen.ts.
 */
function PreopenRow({ v }: { v: Valuation }) {
  const p = v.preopen;
  if (!p) return null;
  const phase = preopenPhase(p);
  const aheadCushion = preopenAheadCushion(p);
  const prob = preopenProbLabel(p);
  const aheadProb = preopenAheadProbLabel(p);
  const projected = preopenProjected(p);
  const aheadProjected = preopenAheadProjected(p);
  const side = preopenSide(p);
  const lost = preopenLostNote(p);
  const built = forecastLive(v.check);

  return (
    <div className="px-4 py-1.5 text-xs">
      <FieldRow>
        {phase && (
          <Field label={phase.label} w={W.preopen} title={phase.title}>
            <Val>{phase.seconds === null ? "" : `${phase.seconds} с`}</Val>
          </Field>
        )}
        <Field
          label="запас"
          w={built ? W.cushionBuilt : W.cushion}
          title={preopenCushionTitle(p)}
        >
          <Val w={W.money} color={deltaColor(cushionTone(p.cushion))}>
            {preopenCushion(p)}
          </Val>
          {aheadCushion !== "" && <Ahead w={W.money}>{aheadCushion}</Ahead>}
        </Field>
        {prob !== "" && (
          <Field label="вероятность ⓘ" w={built ? W.probBuilt : W.prob} title={PREOPEN_PROB_NOTE}>
            <Val w={W.sigma}>{prob}</Val>
            {aheadProb !== "" && <Ahead w={W.sigma}>{aheadProb}</Ahead>}
          </Field>
        )}
        {projected !== undefined && (
          <Field
            label="итог"
            w={built ? W.outcomeBuilt : W.outcome}
            title={
              `${metricLabel(v)} на конец окна, если индекс останется на текущем уровне, ` +
              "против ПРОГНОЗА страйка. Не прогноз рынка — арифметика по уже известным " +
              "секундам плюс замершая цена"
            }
          >
            <Val w={W.price}>{formatIndex(projected)}</Val>
            {aheadProjected !== undefined && (
              <Ahead w={W.price}>{formatIndex(aheadProjected)}</Ahead>
            )}
            {side !== "" && (
              <span
                className="num font-medium"
                style={{ color: deltaColor(cushionTone(p.cushion)) }}
              >
                {side}
              </span>
            )}
          </Field>
        )}
        {lost !== "" && <span className="text-amber-300">{lost}</span>}
        <span className="ml-auto flex items-baseline gap-x-[var(--gap-hug)]">
          <Caption>предстарт</Caption>
          <SourceHint />
        </span>
      </FieldRow>
    </div>
  );
}

/**
 * Разбор сверки — окном поверх экрана.
 *
 * Окно, а не раскрытие на месте: карточка рынка липкая, и раскрытие сдвинуло бы
 * вниз всю страницу под курсором оператора, который в эту секунду целится в
 * кнопку ставки. Форма и поведение — как у таблицы горячих клавиш
 * (components/HotkeyHelp.tsx): подложка закрывает по клику, Escape — по клавише.
 *
 * Часы тикают ЗДЕСЬ, а не в родителе (правило hooks/useNow.ts): возраст сырого
 * значения нужен только этому окну, и заставлять ради него перерисовываться
 * липкую полосу раз в секунду незачем.
 */
/**
 * Итог ЗАКОНЧИВШЕГОСЯ окна — одна строка на месте двух ярусов.
 *
 * # Что здесь заменено и почему
 *
 * Ярус прогноза (Ŝ, метка `t−3`, фора, «$ к числу источника») и модель
 * справедливых цен. Все они считаются от ТЕКУЩЕЙ секунды, и у окна, кончившегося
 * час назад, показывали движение цены чужого времени — с подписью, обещающей
 * фору в секундах. Числа при этом продолжали меняться на глазах: страница
 * опрашивала оценку раз в секунду вечно.
 *
 * Здесь стоят три вещи, и все три неизменны при любом заходе на страницу: итог
 * метрики на конец окна, снос от страйка со знаком и исход. Опрос гасится, как
 * только исход подтверждён (lib/valuation.ts, valuationPollMs).
 *
 * # Почему сторона показывается ДО подтверждения биржи
 *
 * Потому что она следует из тех же двух чисел, которыми рынок и резолвится, а
 * подтверждение из Gamma доезжает до двух минут. Но названа она осторожно:
 * расчётная сторона стоит стрелкой у сноса, а исход биржи — отдельной пометкой,
 * и пока его нет, там прямо написано «не подтверждён». Различие то же, что у
 * ADR-0009 «Решение 6»: «мы ещё не знаем» — это не «проиграл».
 */
function WindowResult({ v }: { v: Valuation }) {
  const final = v.final;
  if (!final) return null;

  const drift = finalDriftLabel(final);
  const side = finalSideLabel(final);
  const outcome = finalOutcomeLabel(final);
  const agrees = finalAgrees(final);
  const tone = deltaColor(finalDriftTone(final));

  return (
    <div className="border-b border-[var(--color-border)] px-4 py-1.5 text-xs">
      <FieldRow>
        <Field tag label="итог" title={finalTitle(v)} w={W.pred}>
          <Val w={W.price}>{formatIndex(final.published)}</Val>
        </Field>

        {/* Снос со знаком и расчётная сторона — вместе и одним цветом: число
            отвечает «насколько», сторона «в чью пользу», и второе читается
            раньше первого. Тот же приём и те же цвета, что у «итога» в ярусе
            замка: это одна и та же величина, только уже случившаяся. */}
        {drift !== "" && (
          <span className="num" style={{ color: tone }}>
            {drift}
            {side !== "" && <span className="ml-1 font-medium">→ {side}</span>}
          </span>
        )}

        {/* Исход биржи. Подтверждённый — обычным тоном и с галочкой, ждущий
            подтверждения — приглушённым: это не тревога, а штатные две минуты
            между концом окна и ответом Gamma. */}
        {outcome !== "" && (
          <span
            className={final.resolved ? "font-medium" : "text-[var(--color-muted)]"}
            style={
              final.resolved && final.winner
                ? { color: deltaColor(finalWinnerTone(final)) }
                : undefined
            }
          >
            {outcome}
            {final.resolved && final.winner && " ✓"}
          </span>
        )}

        {/* Расхождение расчётной стороны с исходом биржи — ТРЕВОГА, и вот
            почему: сторона выводится из тех же двух чисел, по которым рынок
            резолвится, и разойтись они могут только если мы читаем не тот
            отчёт. Это утверждение о наших данных, а не об ошибке модели. */}
        {agrees === false && (
          <span
            className="text-amber-300"
            title="сторона по сносу не совпала с исходом биржи: мы читаем не тот отчёт"
          >
            расхождение со стороной по сносу
          </span>
        )}

        {/* Причина, когда итога нет: журнал не знает этого окна. Состояние
            штатное — страницу открывают и на окне, которого трейдер не
            котировал, — и молчать о нём нельзя: пустое место читается как ноль. */}
        {!final.published && v.reason !== "" && (
          <span className="text-[var(--color-muted)]">{v.reason}</span>
        )}
      </FieldRow>
    </div>
  );
}

function CheckDetails({ v, onClose }: { v: Valuation; onClose: () => void }) {
  const nowMs = useNow(1000);

  // Escape закрывает окно. Боевой режим при этом гасит глобальный слушатель
  // (store/hotkeys.ts) — как и при закрытии таблицы клавиш, и это направление
  // безопасное: снятое оружие не тратит денег.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const check = v.check;
  const rawMs = rawAge(check, nowMs);
  const idxMs = indexAge(v, nowMs);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-5 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-medium">Сверка индекса</h2>
          <span className="text-xs text-[var(--color-muted)]">Esc — закрыть</span>
        </div>

        {/* Причина словами, а не прочерки молча: пустота на месте числа читается
            как ноль, а ноль здесь — это цена. */}
        {!check && <p className="text-amber-300">{emptyNote(v)}</p>}
        {check && (
          // Колонки, а не строка: три числа одной величины сравнивают глазом по
          // вертикали, и выравнивание здесь суть, а не оформление. Ради него
          // разбор и живёт в окне — в строке карточки его не построить.
          <div className="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-3 gap-y-0.5">
            <Line
              label="сырой"
              value={formatIndex(check.raw)}
              note={Number.isFinite(rawMs) && rawMs > 10_000 ? formatAge(rawMs) : ""}
              title="индекс Chainlink без усреднения: из него пересчитывается метрика"
            />
            <Line
              label="наш пересчёт"
              value={formatIndex(check.own)}
              note={coverageLabel(check)}
              title={
                "среднее сырого ряда по окну [t−W−2, t−3] — та же величина, что у " +
                "Polymarket, но известная на три секунды раньше"
              }
            />
            {/* Фора отдельной строкой, и только она её и показывает: «наш
                пересчёт» считается на метку опубликованного значения и обязан с
                ним совпадать — это сверка, а не опережение. */}
            <Line
              label="мы уже знаем"
              value={formatIndex(check.ahead)}
              note={aheadLabel(check)}
              title={
                "отчёт, которого источник ещё не публиковал: ему нужен сырой ряд " +
                "только до t−3, поэтому по нашему ряду считается метка на три " +
                "секунды новее. Это не прогноз — все входы уже существуют"
              }
            />
            {/* Достройка — СРАЗУ под «мы уже знаем»: это то же число, но по
                ряду, продолженному спотом, и сравнивают их по вертикали. */}
            <Line
              label="достройка"
              value={forecastValue(v.check) ? formatIndex(forecastValue(v.check)) : "—"}
              note={forecastNote(v.check) || forecastGainLabel(v.check)}
              tone={forecastTone(v.check)}
              title={
                "сырой ряд, продолженный спотом биржи. Спот едет по тому же проводу " +
                "раньше индекса, поэтому хвост восстанавливается оценкой — это не " +
                "прогноз цены, а уже случившееся, но ещё не доехавшее"
              }
            />
            {v.check?.forecast && (
              <Line
                label="спот"
                value={
                  v.check.forecast.spot_price ? formatIndex(v.check.forecast.spot_price) : "—"
                }
                note={forecastSpotNote(v.check.forecast)}
                title={
                  "опорный ряд достройки. Котирован в USDT, а индекс — в USD-кластере " +
                  "Chainlink: между ними около 63 долларов базиса, который ещё и " +
                  "дрейфует. Поэтому достройка работает в ПРИРАЩЕНИЯХ, где базис " +
                  "сокращается целиком, а сравнивать эти два уровня напрямую нельзя"
                }
              />
            )}
            {v.check?.forecast && (
              <Line
                label="самосверка"
                value={forecastErrValue(v.check.forecast)}
                note={forecastErrNote(v.check.forecast)}
                tone={forecastTone(v.check)}
                title={
                  "каждая оценка через секунду с небольшим встречается со своим " +
                  "настоящим кадром, и разница копится в скользящее окно. Она же и " +
                  "выключатель: за порогом достройка прекращает публикацию, а не " +
                  "портит её тихо. Доллары тут читать нельзя: они движутся и от " +
                  "цены актива, и от волатильности часа — судить надо по доле от " +
                  "«ничего не делать», где единица означает «достройка не окупается»"
                }
              />
            )}
            {v.check?.forecast && forecastLeverNote(v.check.forecast) && (
              <Line
                label="по плечу"
                value=""
                note={forecastLeverNote(v.check.forecast)}
                title={
                  "та же самосверка, разложенная по ПЛЕЧУ оценки — расстоянию от " +
                  "последней сошедшейся пары до оценённой секунды. Медиана по всему " +
                  "окну смешивает величины, отличающиеся втрое, а доля каждого плеча " +
                  "задаётся доставкой провода, а не нами: без разложения движение " +
                  "этой доли неотличимо от движения модели"
                }
              />
            )}
            <Line
              label="Polymarket"
              value={formatIndex(v.index)}
              note={Number.isFinite(idxMs) && idxMs > 10_000 ? formatAge(idxMs) : ""}
              title="опубликованное значение метрики: по нему рынок и резолвится"
            />
            {/* Проекция при замершей цене — СРАЗУ под опубликованным числом:
                она отвечает, куда это самое число уедет само, без единого
                нового движения рынка. */}
            {frozenLimit(v.check) !== undefined && (
              <Line
                label="если цена замрёт"
                value={formatIndex(frozenLimit(v.check))}
                note={frozenLimitNote(v.check)}
                title={
                  "куда придёт МЕТРИКА, если сырая цена останется на текущем уровне. " +
                  "Разница с опубликованным числом — это снос, уже заложенный в него: " +
                  "он случится оттого, что старые секунды покидают окно усреднения. " +
                  "Предел равен сырой цене по построению — среднее постоянного ряда " +
                  "и есть эта постоянная"
                }
              />
            )}
            {frozenPath(v.check) !== "" && (
              <Line
                label="по дороге"
                value=""
                note={frozenPath(v.check)}
                title={
                  "путь к пределу. Он идёт С ПЕРЕЛЁТОМ и линейной интерполяцией не " +
                  "восстанавливается: форма зависит от того, какие секунды ВЫПАДАЮТ " +
                  "из окна усреднения, а не только от того, чем оно добивается"
                }
              />
            )}
            {/* Сверка страйка — здесь же, а не в строке индекса: вопрос один и
                тот же («сходимся ли мы с биржей»), просто про другое число. */}
            {strikeCheckNote(v) !== "" && (
              <Line
                label="страйк"
                value={formatIndex(v.strike)}
                note={strikeCheckNote(v)}
                tone={strikeCheckTone(v) === "warn" ? "warn" : "flat"}
                title={
                  "цена на начало окна. Сверяется с числом самой биржи: расхождение " +
                  "означает, что оценка всего окна смещена ровно на эту величину"
                }
              />
            )}
            <Line
              label="расхождение"
              value={diffLabel(check)}
              note={accuracyLabel(check)}
              tone={diffTone(check) === "warn" ? "warn" : "flat"}
              title={
                "наш пересчёт минус опубликованное. На полных окнах измерялось " +
                "нулевым; полдоллара означает, что сошлось не так, как должно"
              }
            />
          </div>
        )}

        <p className="mt-4 text-[var(--color-muted)]">{SOURCE_NOTE}.</p>
      </div>
    </div>
  );
}

/**
 * Оговорка о происхождении числа — значком.
 *
 * Прежде она стояла на экране полной строкой, и довод был такой: без неё аларм
 * читается как гарантия исхода, а он утверждение о числе, которое раздаёт
 * Polymarket, — с подписанным отчётом Chainlink оно не сверялось.
 *
 * Довод остаётся в силе, меняется его цена. Строка в семьдесят знаков висела в
 * ЛИПКОЙ полосе постоянно, то есть оплачивалась всей высотой экрана, отнятой у
 * ленты и накопления; прочитывают её один раз — когда разбираются, чем вообще
 * является панель. Значок держит то же обещание дешевле: оговорка остаётся НА
 * экране (её видно, и видно, что она есть), а текст открывается наведением.
 * Целиком, отдельным абзацем, он по-прежнему стоит в окне разбора — там, куда
 * идут с вопросом «чему тут верить».
 *
 * Значок повторяется и в плашке тревоги: оговорка относится к ней в первую
 * очередь, и отсылать за ней в другой конец карточки нельзя.
 */
function SourceHint() {
  return (
    <span
      className="cursor-help text-[10px] leading-tight opacity-70"
      title={SOURCE_NOTE}
      aria-label={SOURCE_NOTE}
    >
      ⓘ
    </span>
  );
}

/** Подпись группы: она отвечает, про что стоящие рядом числа. */
function Caption({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">{children}</span>
  );
}

/** Строка «подпись — число — пояснение». */
function Line({
  label,
  value,
  note,
  title,
  tone = "flat",
}: {
  label: string;
  value: string;
  note: string;
  title: string;
  tone?: "flat" | "warn";
}) {
  return (
    <Fragment>
      <span className="text-[var(--color-muted)]" title={title}>
        {label}
      </span>
      <span
        className="num text-right font-medium"
        style={tone === "warn" ? { color: "var(--color-warn)" } : undefined}
      >
        {value}
      </span>
      <span className="text-[var(--color-muted)]">{note}</span>
    </Fragment>
  );
}

/**
 * Чем мерить «замок вот-вот захлопнётся».
 *
 * Выбор оператора, а не константа: честного единственного ответа нет. На АЛАРМ
 * режим не влияет — тот считается арифметикой.
 *
 * # Почему режим уехал на СЕРВЕР
 *
 * Прежде он лежал в настройках вкладки. Так больше нельзя: тем же режимом судит
 * подсистема алертов в трейдере, поднимая сквозную карточку со сделкой, и
 * настройка, живущая в localStorage одного браузера, означала бы, что панель
 * говорит об окне одно, а карточка — другое. Теперь это ключ Redis (lock:mode),
 * один на установку, и `current` приходит с сервера вместе с самим решением.
 *
 * Отсюда же отсутствие локального состояния у кнопок: нажатие пишет ключ и ждёт
 * следующего ответа /valuation. Подсвети мы выбор сразу, оператор видел бы режим,
 * которым решение ещё не посчитано.
 */
function ModePicker({ current }: { current: LockMode | undefined }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function pick(mode: LockMode) {
    setBusy(true);
    try {
      await api.setLockSettings({ mode });
      // Оценка перечитывается немедленно: иначе режим сменился бы на кнопке, а
      // числа под ней остались бы посчитанными прежним — секунду, но ровно в ту
      // секунду, когда на них смотрят.
      void queryClient.invalidateQueries({ queryKey: ["valuation"] });
      void queryClient.invalidateQueries({ queryKey: ["lock", "settings"] });
    } finally {
      setBusy(false);
    }
  }

  const modes: { key: LockMode; label: string; title: string }[] = [
    {
      key: "mean",
      label: "сред",
      title:
        "вдвое больше ДОСТИЖИМОГО среднего отклонения (p90) за тот же " +
        "горизонт. Требуемый скачок — это требование к среднему по неизвестным " +
        "секундам, и сравнивать его с наблюдённым пиком значит сравнивать " +
        "разные величины",
    },
    {
      key: "empirical",
      label: "факт",
      title:
        "вдвое больше наблюдённого максимума за тот же горизонт. Ничего не " +
        "предполагает о распределении — а хвосты у BTC толстые: попадался шаг " +
        "$31.50 за секунду при σ 3.26",
    },
    {
      key: "sigma",
      label: "σ",
      title:
        "пять разбросов остатка по модели. Привычно, но занижает хвосты: " +
        "десятисигмовый шаг случился за пять минут записи",
    },
    { key: "fixed", label: "$", title: "фиксированный порог в долларах" },
  ];
  return (
    <div className="flex gap-1">
      {modes.map((m) => (
        <button
          key={m.key}
          type="button"
          disabled={busy}
          onClick={() => void pick(m.key)}
          title={m.title}
          className={`rounded px-1.5 py-0.5 text-[10px] disabled:opacity-50 ${
            current === m.key
              ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
              : "text-[var(--color-muted)] hover:text-[var(--color-fg)]"
          }`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Цвет ЗНАКА: плюс зелёный, минус красный.
 *
 * Для отклонений — «на сколько это число отличается от опубликованного». Знак
 * тут говорит о величине, а не о направлении будущего хода.
 */
function deltaColor(tone: "up" | "down" | "flat"): string {
  if (tone === "up") return "var(--color-buy)";
  if (tone === "down") return "var(--color-sell)";
  return "var(--color-muted)";
}

/**
 * Цвет НАПРАВЛЕНИЯ: «индексу надо вырасти» красное, «упасть» зелёное.
 *
 * Соглашение ОБРАТНОЕ к цвету знака выше, и это не небрежность: здесь число
 * отвечает на вопрос «куда должна пойти цена, чтобы исход перевернулся», а
 * рост в терминале красный. Слить их в одну функцию значило бы покрасить
 * «нужно вырасти на 18 $» зелёным — то есть сказать «хорошая новость» там, где
 * речь о том, что позицию перевернут.
 */
function directionColor(tone: "up" | "down" | "flat"): string {
  if (tone === "up") return "var(--color-sell)";
  if (tone === "down") return "var(--color-buy)";
  return "var(--color-muted)";
}

/**
 * Цвет требуемого скачка. На грани — ОБЫЧНЫЙ цвет текста, а не приглушённый:
 * приглушить значение значило бы сказать «не важно» ровно в том положении, где
 * исход решает следующий тик.
 */
function flipColor(cushion: string | null | undefined): string | undefined {
  const tone = flipTone(cushion);
  return tone === "flat" ? undefined : directionColor(tone);
}

